import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { useCallback } from 'react';
import { useRealtimeScope } from '@/shared/hooks/useRealtimeScope';
import { useSupabaseRealtime } from '@/shared/hooks/useSupabaseRealtime';
import { rowToNotification } from '@/shared/services/supabaseNotificationService';
import { useNotificationStore } from '@/shared/stores/notificationStore';
import type { Tables } from '@/shared/types/database';

type NotificationRow = Tables<'notifications'>;

/**
 * Realtime notifications subscription (P7).
 *
 * Streams `public.notifications` changes addressed to the current profile into
 * the notification store via the local-only `upsertRealtimeNotification` /
 * `removeRealtimeNotification` setters (no service round-trips). Scoped
 * server-side by `profile_id` and additionally guarded by `family_id`. A no-op
 * when realtime is disabled (see {@link useSupabaseRealtime}).
 *
 * @module features/notifications/hooks/useRealtimeNotifications
 */
export function useRealtimeNotifications(): void {
  const { familyId, profileId } = useRealtimeScope();
  const upsertRealtimeNotification = useNotificationStore(
    (s) => s.upsertRealtimeNotification
  );
  const removeRealtimeNotification = useNotificationStore(
    (s) => s.removeRealtimeNotification
  );
  const refetch = useNotificationStore((s) => s.refetch);

  const handleChange = useCallback(
    (payload: RealtimePostgresChangesPayload<NotificationRow>) => {
      if (payload.eventType === 'DELETE') {
        const oldId = payload.old.id;
        if (oldId) {
          removeRealtimeNotification(oldId);
        }
        return;
      }

      const row = payload.new;
      // Defense in depth: only surface rows for the current user/family even if
      // the server-side filter is ever absent.
      if (profileId !== null && row.profile_id !== profileId) {
        return;
      }
      if (familyId !== null && row.family_id !== familyId) {
        return;
      }
      upsertRealtimeNotification(rowToNotification(row));
    },
    [
      familyId,
      profileId,
      upsertRealtimeNotification,
      removeRealtimeNotification,
    ]
  );

  useSupabaseRealtime<NotificationRow>({
    table: 'notifications',
    enabled: profileId !== null,
    ...(profileId !== null && { filter: `profile_id=eq.${profileId}` }),
    onChange: handleChange,
    // Changes streamed while the channel was down are lost — refetch on
    // recovery to close the gap.
    onReconnect: () => void refetch(),
  });
}
